import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Clock3, ImagePlus, MessageCircle, X } from "lucide-react";
import { API, QR_URL, openWhatsApp, parsePrice, trackEvent } from "@/lib/site";
import { tiersOf } from "./EventsSection";

const EASE = [0.16, 1, 0.3, 1];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CoverModal = ({ event, onClose }) => {
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", utr: "" });
  const [proof, setProof] = useState("");
  const [proofPreview, setProofPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [entryType, setEntryType] = useState("");
  const [qty, setQty] = useState(1);
  useEffect(() => { setSubmitted(null); setForm({ name: "", phone: "", email: "", utr: "" }); setProof(""); setProofPreview(""); setEntryType(""); setQty(1); }, [event]);

  if (!event) return <AnimatePresence>{null}</AnimatePresence>;
  const tiers = tiersOf(event);
  const options = tiers.length ? tiers : [{ key: "cover", label: "Cover", price: event.price }];
  const selected = options.find((t) => t.key === entryType) || options[0];
  const total = parsePrice(selected.price) * qty;

  const uploadProof = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const response = await axios.post(`${API}/upload-proof`, data);
      setProof(response.data.path);
      setProofPreview(`${BACKEND_URL}${response.data.url}`);
    } catch { alert("Screenshot upload failed — please try again."); }
    setUploading(false);
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    if (!proof || submitting) return;
    setSubmitting(true);
    trackEvent("cover_charge_submit", { event_name: event.title, entry_type: selected.label, quantity: qty, value: total, currency: "INR" });
    try {
      const response = await axios.post(`${API}/cover-charges`, { ...form, payment_proof: proof, event: event.title, amount: total, entry_type: selected.label, quantity: qty });
      setSubmitted(response.data.id);
    } catch { setSubmitted("SOHO-PENDING"); }
    setSubmitting(false);
  };

  return (
    <AnimatePresence>
      {event && (
        <motion.div className="modal-backdrop" role="dialog" aria-modal="true" data-testid="cover-charge-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
          <motion.div className="payment-modal"
            initial={{ opacity: 0, y: 44, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 26, scale: 0.98 }} transition={{ duration: 0.55, ease: EASE }}>
            <button className="close-modal" onClick={onClose} aria-label="Close" data-testid="close-cover-modal"><X/></button>
            {submitted ? (
              <div className="success-state">
                <div className="success-icon pending"><Clock3/></div>
                <p className="kicker">Verification pending</p>
                <h2>We’re checking<br/><em>your payment.</em></h2>
                <p className="muted" data-testid="payment-success-message">Your reference is <strong>{submitted}</strong> — {selected.label} × {qty}. Our team will verify your UPI payment and screenshot, then email your gate pass. <b>The pass is valid at the gate only after verification.</b></p>
                <button className="button button-pink full" onClick={() => openWhatsApp(`Hi Soho Goa, I paid for ${event.title} (${selected.label} × ${qty}). Reference: ${submitted}`, event.title)} data-testid="payment-whatsapp-button"><MessageCircle size={17}/> Send payment details</button>
              </div>
            ) : (
              <>
                <div className="modal-heading">
                  <p className="kicker">{event.title}</p>
                  <h2>Get your <em>cover.</em></h2>
                  <p className="muted">Pick your entry, pay via UPI, attach the screenshot — we’ll email your verified gate pass.</p>
                </div>
                <div className="payment-layout">
                  <div className="qr-box"><img src={QR_URL} alt="Official Soho Goa UPI payment QR" data-testid="upi-qr-image"/><span>Scan to pay via UPI</span></div>
                  <form onSubmit={submitPayment} data-testid="cover-charge-form">
                    <div className="tier-picker">
                      {options.map((t) => (
                        <button type="button" key={t.key} className={`tier-pill ${selected.key === t.key ? "active" : ""}`} onClick={() => setEntryType(t.key)} data-testid={`entry-type-${t.key}`}>
                          {t.label}<b>{t.price}</b>
                        </button>
                      ))}
                    </div>
                    <div className="qty-row">
                      <span>Passes</span>
                      <div className="qty-stepper">
                        <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} aria-label="One less pass" data-testid="qty-minus">−</button>
                        <strong data-testid="qty-value">{qty}</strong>
                        <button type="button" onClick={() => setQty(Math.min(10, qty + 1))} aria-label="One more pass" data-testid="qty-plus">+</button>
                      </div>
                    </div>
                    <div className="amount-row"><span>{selected.label} × {qty}</span><strong data-testid="cover-total">₹{total.toLocaleString("en-IN")}</strong></div>
                    <input required placeholder="Your name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="cover-name-input"/>
                    <input required type="tel" placeholder="Mobile number" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="cover-phone-input"/>
                    <input required type="email" placeholder="Email — your pass is sent here" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="cover-email-input"/>
                    <input required placeholder="UPI transaction ID / UTR" value={form.utr} onChange={e => setForm({...form, utr: e.target.value})} data-testid="cover-utr-input"/>
                    <div className="proof-upload">
                      {proofPreview && <img className="proof-preview" src={proofPreview} alt="Payment screenshot preview" data-testid="cover-proof-preview"/>}
                      <label className="poster-upload-btn" data-testid="cover-proof-button"><input type="file" accept="image/*" hidden onChange={uploadProof} data-testid="cover-proof-input"/><ImagePlus size={15}/>{uploading ? "Uploading screenshot…" : proof ? "Screenshot attached — tap to replace" : "Attach payment screenshot (required)"}</label>
                    </div>
                    <button className="button button-pink full" type="submit" disabled={!proof || uploading || submitting} data-testid="submit-cover-charge-button">{submitting ? "Creating your pass…" : `I’ve paid ₹${total.toLocaleString("en-IN")} — request my pass`} <ArrowRight size={16}/></button>
                    <p className="form-note"><Clock3 size={13}/> Pass is emailed after staff verify your payment</p>
                  </form>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
